import {
  HttpClient,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import {
  Injectable,
  inject,
  signal,
} from '@angular/core';
import { Observable, forkJoin, map, of, tap, catchError } from 'rxjs';

import { API_V1_URL } from '../../../core/config/api.config';
import { BestellungArtikel } from '../models/bestellposition.model';
import { BestellungStatus } from '../models/bestellung-status.enum';
import { Bestellung, BestellungTyp } from '../models/bestellung.model';
import { AuthService } from '../../../core/services/auth.service';
import { ArtikelEinheitDto } from '../../../shared/models/artikel-einheit.model';

export interface BestellungHeader {
  id: string;
  filialNummer: number;
  filiale: string;
  lieferdatum: string;
  restzeit: string;
  bestellDatumVon: string;
  bestellDatumBis: string;
  uhrzeitVon: string;
  uhrzeitBis: string;
  orderCanBeChangedContinuously: boolean;
  abgeschlossen: boolean;
  status: BestellungStatus;
  lockedByName?: string | null;
  gesamtBestellwert: number;
}

export interface BestellungKonfiguration {
  lotSizeActive: boolean;
  quantityLotInsteadOfPcsForLG1Active: boolean;
  quantityLotInsteadOfPcsForLG2Active: boolean;
  orderCanBeChangedContinuously: boolean;
  quantityLotLG0DoesLimitToOrderingQuantity: boolean;
  quantityLotLG3DoesLimitToOrderingQuantity: boolean;
  quantityLotLG4DoesLimitToOrderingQuantity: boolean;
}

export interface BestellungDetailView {
  header: BestellungHeader;
  artikel: BestellungArtikel[];
}

export interface VerfuegbarerArtikel {
  /** Units delivered by the shared article API. Order quantity rules remain unchanged. */
  einheiten?: readonly ArtikelEinheitDto[];
  artikelnummer: number;
  artikelName: string | null;
  warengruppeBezeichnung: string | null;
  oberwarengruppeId: number | null;
  warengruppeId: number | null;
  vkPreis: number | null;
  losgroesseType: number;
  bestellmenge: number | null;
  halbePkgErlaubt: boolean;
}

export interface BestellmengeItemRequest {
  artikelnummer: number;
  menge: number;
}

export interface WeeklyData {
  kw: string;
  bestellt: number;
  verkauft: number;
}

export interface WeeklyDetail extends WeeklyData {
  differenz: number;
  retoure: number;
  entlastet: number;
}

export interface BestellassistentData {
  empfehlung: number;
  averageSales: number;
  averageReturns: number;
  salesRate: number;
  weeklyData: WeeklyData[];
  weeklyDetails: WeeklyDetail[];
  vorbestelltMenge: number | null;
  gesamtBestellt: number;
  gesamtVerkauft: number;
  gesamtRetouren: number;
  gesamtEntlastet: number;
  trend: Trendbewertung;
}

interface BackendBestellungDto {
  id: string;
  filialNummer: number;
  filialName: string | null;
  liefertag: string;
  datumVon: string;
  datumBis: string;
  uhrzeitVon: string;
  uhrzeitBis: string;
  zeitraum: string;
  abgeschlossen: boolean;
  art: number | null;
  bestellungart: string | null;
  tournummer: number | null;
  status: BestellungStatus | string | number;
  istKritisch: boolean;
  restzeit: string;
}

interface BackendBestellungDetailDto extends BackendBestellungDto {
  gesamtBestellwert: number;
  positionen: BackendBestellungPositionDto[];
}

interface BackendBestellungPositionDto {
  artikelnummer: number;
  artikelName: string | null;
  oberwarengruppeId: number | null;
  warengruppeId: number | null;
  bestellmengeT1: number;
  vorschlag: number | null;
  status: boolean | null;
  vkPreis: number | null;
  gesperrt: boolean | null;
  mindestmenge: number | null;
  maxMenge: number | null;
  vorbestelltMenge: number | null;
  bestellmengenEinheit: number | null;
  bestellEinheitName: string | null;
  losgroesseType: number | string;
  halbePkgErlaubt: boolean;
  sortierschluessel: string | null;
}

interface WarengruppeDto {
  id: number;
  name: string;
}

export interface BestellungLockSession {
  lockId: string;
  expiresAt: string;
}

type Trendbewertung =
  | 'KeineDaten'
  | 'Steigend'
  | 'Stabil'
  | 'Sinkend';

interface AssistentBestellungKpiDto {
  vorbestelltMenge: number | null;
  gesamtBestellt: number;
  gesamtVerkauft: number;
  verkaufsquote: number | null;
  gesamtRetouren: number;
  gesamtEntlastet: number;
  empfohleneMenge: number | null;
  trend: Trendbewertung;
}

interface AssistentBestellungWochendetailDto {
  jahr: number;
  kw: number;
  bestellt: number;
  verkauft: number;
  delta: number;
  retoure: number;
  entlaste: number;
}

interface QuantityRule {
  effectiveLosgroesseType: number;
  displayEinheit: string;
  quantityStep: number | null;
  buttonStep: number;
  inputStep: string;
  enforceMinMax: boolean;
  allowsHalf: boolean;
  quantityStepLabel: string;
  calculationFactor: number;
  minMaxValidationFactor: number;
  showQuantityCalculation: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BestellungenService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiBaseUrl = API_V1_URL;
  private readonly activeLocks = new Map<string, BestellungLockSession>();

  private readonly offeneBestellungenAnzahlState =
    signal<number | undefined>(undefined);

  private pendingBestellungStatusCheck: {
    id: string;
    status: BestellungStatus;
  } | null = null;

  readonly offeneBestellungenAnzahl =
    this.offeneBestellungenAnzahlState.asReadonly();

  getBestellungen(): Observable<Bestellung[]> {
    return forkJoin({
      bestellungen: this.http.get<BackendBestellungDto[]>(`${this.apiBaseUrl}/bestellungen/all`),
      konfiguration: this.getBestellungKonfiguration(),
    }).pipe(
      map(({ bestellungen, konfiguration }) =>
        bestellungen.map((item) => this.mapBestellung(item, konfiguration))
      )
    );
  }

  getOffeneBestellungenAnzahl(): Observable<number> {
    return this.http
      .get<number>(
        `${this.apiBaseUrl}/bestellungen/anzahl-offen`
      )
      .pipe(
        tap((anzahl) => {
          this.offeneBestellungenAnzahlState.set(
            anzahl
          );
        })
      );
  }

  rememberBestellungStatusBeforeDetail(
    id: string,
    status: BestellungStatus
  ): void {
    this.pendingBestellungStatusCheck = {
      id,
      status,
    };
  }

  consumeBestellungStatusBeforeDetail(): {
    id: string;
    status: BestellungStatus;
  } | null {
    const pendingStatus =
      this.pendingBestellungStatusCheck;

    this.pendingBestellungStatusCheck = null;

    return pendingStatus;
  }

  getBestellungKonfiguration(): Observable<BestellungKonfiguration> {
    return this.http
      .get<BestellungKonfiguration>(`${this.apiBaseUrl}/settings/bestellung`)
      .pipe(catchError(() => of(this.getDefaultBestellungKonfiguration())));
  }

  updateBestellungKonfiguration(
    orderCanBeChangedContinuously: boolean
  ): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/settings/bestellung`, {
      orderCanBeChangedContinuously,
    });
  }

  getBestellungDetail(id: string): Observable<BestellungDetailView> {
    return forkJoin({
      detail: this.http.get<BackendBestellungDetailDto>(`${this.apiBaseUrl}/bestellungen/${id}`),
      warengruppen: this.http.get<WarengruppeDto[]>(`${this.apiBaseUrl}/bestellungen/warengruppen`),
      konfiguration: this.getBestellungKonfiguration(),
    }).pipe(
      map(({ detail, warengruppen, konfiguration }) =>
        this.mapBestellungDetail(detail, warengruppen, konfiguration)
      )
    );
  }

  getVerfuegbareArtikel(id: string): Observable<VerfuegbarerArtikel[]> {
    return this.http.get<VerfuegbarerArtikel[]>(
      `${this.apiBaseUrl}/bestellungen/${id}/verfuegbare-artikel`
    );
  }

  addArtikel(id: string, artikelnummer: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiBaseUrl}/bestellungen/${id}/positionen/${artikelnummer}`,
      null
    );
  }

  saveBestellmengen(id: string, positionen: BestellmengeItemRequest[]): Observable<void> {
    return this.http.put<void>(
      `${this.apiBaseUrl}/bestellungen/${id}/positionen`,
      positionen
    );
  }

  saveBestellmengenFinal(id: string, positionen: BestellmengeItemRequest[]): Observable<void> {
    return this.http.put<void>(
      `${this.apiBaseUrl}/bestellungen/${id}/positionen/speichern`,
      positionen
    );
  }

  saveBestellmengenBeforeUnload(
    id: string,
    positionen: BestellmengeItemRequest[]
  ): void {
    if (!positionen.length) {
      return;
    }

    const token = this.authService.getToken();

    if (!token) {
      return;
    }

    void fetch(`${this.apiBaseUrl}/bestellungen/${id}/positionen`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(positionen),
    });
  }

  abschliessenBestellung(id: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiBaseUrl}/bestellungen/${id}/abschliessen`,
      null
    );
  }

  acquireBestellungLock(id: string): Observable<BestellungLockSession> {
    const key = `bestellung:${id}`;

    return this.http
      .post<BestellungLockSession>(`${this.apiBaseUrl}/locks/acquire`, {
        keys: [key],
      })
      .pipe(
        tap((response) => this.activeLocks.set(key, response))
      );
  }

  heartbeatBestellungLock(
    id: string
  ): Observable<BestellungLockSession | null> {
    const key = `bestellung:${id}`;
    const lockSession = this.activeLocks.get(key);

    if (!lockSession) {
      return of(null);
    }

    return this.http
      .put<BestellungLockSession>(
        `${this.apiBaseUrl}/locks/${lockSession.lockId}/heartbeat`,
        null
      )
      .pipe(
        tap((response) => this.activeLocks.set(key, response))
      );
  }

  getBestellungLockSession(id: string): BestellungLockSession | null {
    return this.activeLocks.get(`bestellung:${id}`) ?? null;
  }

  releaseBestellungLock(id: string): Observable<void> {
    const key = `bestellung:${id}`;
    const lockSession = this.activeLocks.get(key);

    if (!lockSession) {
      return of(void 0);
    }

    return this.http
      .delete<void>(
        `${this.apiBaseUrl}/locks/${lockSession.lockId}`
      )
      .pipe(
        tap(() => this.activeLocks.delete(key))
      );
  }

  releaseBestellungLockBeforeUnload(id: string): void {
    const key = `bestellung:${id}`;
    const lockSession = this.activeLocks.get(key);

    if (!lockSession) {
      return;
    }

    const token = this.authService.getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    this.activeLocks.delete(key);

    void fetch(
      `${this.apiBaseUrl}/locks/${lockSession.lockId}`,
      {
        method: 'DELETE',
        keepalive: true,
        headers,
      }
    );
  }

  clearBestellungLock(id: string): void {
    this.activeLocks.delete(`bestellung:${id}`);
  }

  getBestellassistentData(
    bestellungId: string,
    artikelnummer: number
  ): Observable<BestellassistentData> {
    const uidParams = new HttpParams().set(
      'uid',
      bestellungId
    );

    return forkJoin({
      bestellungKpi:
        this.http.get<AssistentBestellungKpiDto>(
          `${this.apiBaseUrl}/bestellungen/assistent/${artikelnummer}/bestellung-kpi`,
          {
            params: uidParams,
          }
        ),

      bestellungWochen:
        this.http.get<
          AssistentBestellungWochendetailDto[]
        >(
          `${this.apiBaseUrl}/bestellungen/assistent/${artikelnummer}/bestellung-wochen`,
          {
            params: uidParams,
          }
        ),
    }).pipe(
      map(
        ({
          bestellungKpi,
          bestellungWochen,
        }) =>
          this.mapBestellassistentData(
            bestellungKpi,
            bestellungWochen
          )
      )
    );
  }

  private mapBestellung(
    item: BackendBestellungDto,
    konfiguration: BestellungKonfiguration
  ): Bestellung {
    const status = this.mapStatus(item.status);

    return {
      id: item.id,

      filialNummer:
        item.filialNummer,

      filialeName:
        item.filialName ??
        `Filiale ${item.filialNummer}`,

      datum:
        this.formatDate(item.liefertag),

      typ:
        this.mapBestellungTyp(
          item.bestellungart
        ),

      status,

      restzeit:
        this.formatRestzeit(
          item.restzeit,
          item.abgeschlossen
        ),

      zeitraum:
        `${this.formatShortDate(
          item.datumVon
        )} - ${this.formatShortDate(
          item.datumBis
        )}`,

      uhrzeitVon:
        item.uhrzeitVon,

      uhrzeitBis:
        item.uhrzeitBis,

      bestellzeitLabel:
        this.getBestellzeitLabel(
          item,
          konfiguration
        ),

      kritisch:
        item.istKritisch,

      lockedByName:
        null,
    };
  }

  private mapBestellungDetail(
    detail: BackendBestellungDetailDto,
    warengruppen: WarengruppeDto[],
    konfiguration: BestellungKonfiguration
  ): BestellungDetailView {
    const warengruppenMap = new Map(warengruppen.map((item) => [item.id, item.name]));
    const status = this.mapStatus(detail.status);

    return {
      header: {
        id: detail.id,
        filialNummer: detail.filialNummer,
        filiale: `${detail.filialNummer} – ${detail.filialName ?? 'Filiale'}`,
        lieferdatum: this.formatDate(detail.liefertag),
        restzeit: this.formatRestzeit(detail.restzeit, detail.abgeschlossen),
        bestellDatumVon:
          detail.datumVon,

        bestellDatumBis:
          detail.datumBis,
        uhrzeitVon: detail.uhrzeitVon,
        uhrzeitBis: detail.uhrzeitBis,
        orderCanBeChangedContinuously: konfiguration.orderCanBeChangedContinuously,
        abgeschlossen: detail.abgeschlossen,
        status,
        lockedByName: null,
        gesamtBestellwert: detail.gesamtBestellwert,
      },
      artikel: detail.positionen.map((position) =>
        this.mapPosition(position, warengruppenMap, konfiguration)
      ),
    };
  }

  private mapPosition(
    position: BackendBestellungPositionDto,
    warengruppenMap: Map<number, string>,
    konfiguration: BestellungKonfiguration
  ): BestellungArtikel {
    const losgroesse = this.normalizeLosgroesse(position.bestellmengenEinheit);
    const einheitName = position.bestellEinheitName?.trim() || 'Stück';
    const losgroesseType = this.mapLosgroesseType(position.losgroesseType);
    const quantityRule = this.getQuantityRule(
      losgroesseType,
      losgroesse,
      einheitName,
      konfiguration
    );

    const warengruppeName = position.warengruppeId
      ? warengruppenMap.get(position.warengruppeId) ?? `Warengruppe ${position.warengruppeId}`
      : 'Ohne Warengruppe';

    const storedMenge = position.bestellmengeT1 ?? 0;
    const vorschlagActual = position.vorschlag ?? storedMenge;
    const empfehlungActual = position.vorbestelltMenge ?? vorschlagActual;

    const menge = this.toInputQuantity(storedMenge, quantityRule.calculationFactor);
    const vorschlag = this.toInputQuantity(vorschlagActual, quantityRule.calculationFactor);
    const empfehlung = this.toInputQuantity(empfehlungActual, quantityRule.calculationFactor);

    return {
      nummer: String(position.artikelnummer),
      name: position.artikelName ?? `Artikel ${position.artikelnummer}`,
      beschreibung: `${warengruppeName} • ${quantityRule.displayEinheit}`,
      preis: position.vkPreis ?? 0,
      vorschlag,
      empfehlung,
      menge,
      einheit: quantityRule.displayEinheit,
      pkg: quantityRule.allowsHalf || position.halbePkgErlaubt,
      min: quantityRule.enforceMinMax ? position.mindestmenge : null,
      max: quantityRule.enforceMinMax ? position.maxMenge : null,
      readonly: position.gesperrt === true,
      changed: false,
      saved: position.status === true,
      serverError: null,
      warengruppeId: position.warengruppeId,
      oberwarengruppeId: position.oberwarengruppeId,
      sortierschluessel:
        position.sortierschluessel?.trim() ||
        String(position.artikelnummer),
      losgroesseType,
      effectiveLosgroesseType: quantityRule.effectiveLosgroesseType,
      losgroesse,
      quantityStep: quantityRule.quantityStep,
      buttonStep: quantityRule.buttonStep,
      inputStep: quantityRule.inputStep,
      enforceMinMax: quantityRule.enforceMinMax,
      quantityStepLabel: quantityRule.quantityStepLabel,
      calculationFactor: quantityRule.calculationFactor,
      minMaxValidationFactor: quantityRule.minMaxValidationFactor,
      showQuantityCalculation: quantityRule.showQuantityCalculation,
    };
  }

  private mapBestellassistentData(
    bestellungKpi: AssistentBestellungKpiDto,
    bestellungWochen: AssistentBestellungWochendetailDto[]
  ): BestellassistentData {
    const sortedWeeks = [
      ...bestellungWochen,
    ].sort(
      (a, b) =>
        a.jahr - b.jahr ||
        a.kw - b.kw
    );

    const weeklyData = sortedWeeks.map(
      (week) => ({
        kw: String(week.kw),
        bestellt: week.bestellt,
        verkauft: week.verkauft,
      })
    );

    const weeklyDetails =
      sortedWeeks.map((week) => ({
        kw: String(week.kw),
        bestellt: week.bestellt,
        verkauft: week.verkauft,
        differenz: week.delta,
        retoure: week.retoure,
        entlastet: week.entlaste,
      }));

    return {
      empfehlung:
        bestellungKpi.empfohleneMenge ?? 0,

      averageSales:
        bestellungKpi.gesamtVerkauft,

      averageReturns:
        bestellungKpi.gesamtRetouren,

      salesRate:
        this.normalizeSalesRate(
          bestellungKpi.verkaufsquote
        ),

      weeklyData,
      weeklyDetails,

      vorbestelltMenge:
        bestellungKpi.vorbestelltMenge,

      gesamtBestellt:
        bestellungKpi.gesamtBestellt,

      gesamtVerkauft:
        bestellungKpi.gesamtVerkauft,

      gesamtRetouren:
        bestellungKpi.gesamtRetouren,

      gesamtEntlastet:
        bestellungKpi.gesamtEntlastet,

      trend:
        bestellungKpi.trend,
    };
  }

  private normalizeSalesRate(value: number | null): number {
    if (value === null) {
      return 0;
    }

    const percentValue = value <= 1 ? value * 100 : value;

    return Math.max(0, Math.min(100, Math.round(percentValue)));
  }

  private getBestellzeitLabel(
    item: Pick<
      BackendBestellungDto,
      | 'datumVon'
      | 'datumBis'
      | 'uhrzeitVon'
      | 'uhrzeitBis'
    >,
    konfiguration: BestellungKonfiguration
  ): string {
    if (
      !konfiguration
        .orderCanBeChangedContinuously
    ) {
      return `${item.uhrzeitVon} - ${item.uhrzeitBis} Uhr`;
    }

    const startDate =
      this.parseIsoDate(item.datumVon);

    const endDate =
      this.parseIsoDate(item.datumBis);

    if (!startDate || !endDate) {
      return 'Durchgehend';
    }

    const now = new Date();

    const [startHour, startMinute] =
      item.uhrzeitVon
        .split(':')
        .map(Number);

    const [endHour, endMinute] =
      item.uhrzeitBis
        .split(':')
        .map(Number);

    const startDateTime =
      new Date(startDate);

    startDateTime.setHours(
      startHour,
      startMinute,
      0,
      0
    );

    const endDateTime =
      new Date(endDate);

    endDateTime.setHours(
      endHour,
      endMinute,
      0,
      0
    );

    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (
      startDate.getTime() ===
      endDate.getTime()
    ) {
      return `${item.uhrzeitVon} - ${item.uhrzeitBis} Uhr`;
    }

    if (
      today.getTime() <
      startDate.getTime()
    ) {
      return `Ab ${item.uhrzeitVon} Uhr`;
    }

    if (
      today.getTime() ===
      startDate.getTime()
    ) {
      if (
        now.getTime() <
        startDateTime.getTime()
      ) {
        return `Heute ab ${item.uhrzeitVon} Uhr`;
      }

      return 'Durchgehend';
    }

    if (
      today.getTime() ===
      endDate.getTime()
    ) {
      return `Bis ${item.uhrzeitBis} Uhr`;
    }

    if (
      today.getTime() >
      endDate.getTime()
    ) {
      return `Bis ${item.uhrzeitBis} Uhr`;
    }

    return 'Durchgehend';
  }

  private getDefaultBestellungKonfiguration(): BestellungKonfiguration {
    return {
      lotSizeActive: false,
      quantityLotInsteadOfPcsForLG1Active: false,
      quantityLotInsteadOfPcsForLG2Active: false,
      orderCanBeChangedContinuously: false,
      quantityLotLG0DoesLimitToOrderingQuantity: false,
      quantityLotLG3DoesLimitToOrderingQuantity: true,
      quantityLotLG4DoesLimitToOrderingQuantity: true,
    };
  }

  private normalizeLosgroesse(value: number | null): number {
    if (value === null || Number.isNaN(value) || value <= 0) {
      return 1;
    }

    return value;
  }

  private getQuantityRule(
    losgroesseType: number,
    losgroesse: number,
    einheitName: string,
    konfiguration: BestellungKonfiguration
  ): QuantityRule {
    const lg = this.normalizeLosgroesse(losgroesse);

    switch (losgroesseType) {
      case 1: {
        const usesLotInput =
          konfiguration.lotSizeActive &&
          konfiguration.quantityLotInsteadOfPcsForLG1Active;

        if (usesLotInput) {
          return this.createQuantityRule({
            effectiveLosgroesseType: 1,
            displayEinheit: `Los / ${this.formatQuantityNumber(lg)}`,
            quantityStep: 1,
            enforceMinMax: true,
            allowsHalf: false,
            quantityStepLabel: 'Nur ganze Losgrößen sind erlaubt.',
            calculationFactor: lg,
            minMaxValidationFactor: lg,
            showQuantityCalculation: true,
          });
        }

        return this.createQuantityRule({
          effectiveLosgroesseType: 1,
          displayEinheit: `${einheitName} / ${this.formatQuantityNumber(lg)}`,
          quantityStep: lg,
          enforceMinMax: true,
          allowsHalf: false,
          quantityStepLabel: `Die Menge muss ein Vielfaches von ${this.formatQuantityNumber(lg)} sein.`,
          calculationFactor: 1,
          minMaxValidationFactor: 1,
          showQuantityCalculation: false,
        });
      }

      case 2: {
        const usesLotInput =
          konfiguration.lotSizeActive &&
          konfiguration.quantityLotInsteadOfPcsForLG2Active;

        if (usesLotInput) {
          return this.createQuantityRule({
            effectiveLosgroesseType: 2,
            displayEinheit: `Los / ${this.formatQuantityNumber(lg)}`,
            quantityStep: 0.5,
            enforceMinMax: true,
            allowsHalf: true,
            quantityStepLabel: 'Nur ganze oder halbe Losgrößen sind erlaubt.',
            calculationFactor: lg,
            minMaxValidationFactor: lg,
            showQuantityCalculation: true,
          });
        }

        return this.createQuantityRule({
          effectiveLosgroesseType: 2,
          displayEinheit: `${einheitName} / ${this.formatQuantityNumber(lg)}`,
          quantityStep: lg / 2,
          enforceMinMax: true,
          allowsHalf: true,
          quantityStepLabel: `Die Menge muss ein Vielfaches von ${this.formatQuantityNumber(lg / 2)} sein.`,
          calculationFactor: 1,
          minMaxValidationFactor: 1,
          showQuantityCalculation: false,
        });
      }

      case 3: {
        const enforceMinMax = konfiguration.lotSizeActive
          ? konfiguration.quantityLotLG3DoesLimitToOrderingQuantity
          : true;

        return this.createQuantityRule({
          effectiveLosgroesseType: 3,
          displayEinheit: `${einheitName} / ${this.formatQuantityNumber(lg)}`,
          quantityStep: 1,
          enforceMinMax,
          allowsHalf: false,
          quantityStepLabel: 'Nur ganze Zahlen sind erlaubt.',
          calculationFactor: lg,
          minMaxValidationFactor: 1,
          showQuantityCalculation: true,
        });
      }

      case 4: {
        const enforceMinMax = konfiguration.lotSizeActive
          ? konfiguration.quantityLotLG4DoesLimitToOrderingQuantity
          : true;

        return this.createQuantityRule({
          effectiveLosgroesseType: 4,
          displayEinheit: `${einheitName} / ${this.formatQuantityNumber(lg)}`,
          quantityStep: 0.5,
          enforceMinMax,
          allowsHalf: true,
          quantityStepLabel: 'Nur ganze oder halbe Zahlen sind erlaubt.',
          calculationFactor: lg,
          minMaxValidationFactor: 1,
          showQuantityCalculation: true,
        });
      }

      default: {
        const enforceMinMax = konfiguration.lotSizeActive
          ? konfiguration.quantityLotLG0DoesLimitToOrderingQuantity
          : true;

        return this.createQuantityRule({
          effectiveLosgroesseType: 0,
          displayEinheit: `${einheitName} / 1`,
          quantityStep: 1,
          enforceMinMax,
          allowsHalf: false,
          quantityStepLabel: 'Nur ganze Zahlen sind erlaubt.',
          calculationFactor: 1,
          minMaxValidationFactor: 1,
          showQuantityCalculation: false,
        });
      }
    }
  }

  private createQuantityRule(rule: {
    effectiveLosgroesseType: number;
    displayEinheit: string;
    quantityStep: number | null;
    enforceMinMax: boolean;
    allowsHalf: boolean;
    quantityStepLabel: string;
    calculationFactor: number;
    minMaxValidationFactor: number;
    showQuantityCalculation: boolean;
  }): QuantityRule {
    return {
      ...rule,
      buttonStep: rule.quantityStep ?? 1,
      inputStep: rule.quantityStep === null ? 'any' : String(rule.quantityStep),
    };
  }

  private toInputQuantity(value: number | null | undefined, factor: number): number {
    const amount = value ?? 0;

    if (factor <= 0) {
      return amount;
    }

    return Number((amount / factor).toFixed(6));
  }

  private formatQuantityNumber(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      maximumFractionDigits: 2,
    }).format(value);
  }

  private mapStatus(
    status: BestellungStatus | string | number
  ): BestellungStatus {
    if (typeof status === 'number') {
      return [
        BestellungStatus.Kritisch,
        BestellungStatus.Unbearbeitet,
        BestellungStatus.InBearbeitung,
        BestellungStatus.Abgeschlossen,
        BestellungStatus.Fertig,
      ][status] ?? BestellungStatus.Unbearbeitet;
    }

    const normalized = String(status)
      .trim()
      .toLowerCase();

    if (normalized === 'kritisch') {
      return BestellungStatus.Kritisch;
    }

    if (
      normalized === 'inbearbeitung' ||
      normalized === 'in_bearbeitung' ||
      normalized === 'in bearbeitung'
    ) {
      return BestellungStatus.InBearbeitung;
    }

    if (normalized === 'abgeschlossen') {
      return BestellungStatus.Abgeschlossen;
    }

    if (normalized === 'fertig') {
      return BestellungStatus.Fertig;
    }

    return BestellungStatus.Unbearbeitet;
  }

  private mapBestellungTyp(bestellungart: string | null): BestellungTyp {
    const value = bestellungart?.trim();

    if (!value) return 'Standard';

    const normalized = value.toLowerCase();

    if (normalized.includes('eil')) return 'Eilbestellung';
    if (normalized.includes('sonder')) return 'Sonderbestellung';

    return value;
  }

  private mapLosgroesseType(value: number | string): number {
    if (typeof value === 'number') return value;

    switch (value) {
      case 'KeineLosgroesse':
        return 0;
      case 'GanzeMitLosgroesseneingabe':
        return 1;
      case 'GanzeUndHalbeMitLosgroesseneingabe':
        return 2;
      case 'GanzeMitStueckeingabe':
        return 3;
      case 'GanzeUndHalbeMitStueckeingabe':
        return 4;
      default:
        return 0;
    }
  }

  private formatDate(value: string): string {
    const date = this.parseIsoDate(value);
    if (!date) return value;

    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  private formatShortDate(value: string): string {
    const date = this.parseIsoDate(value);
    if (!date) return value;

    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
    }).format(date);
  }

  private parseIsoDate(value: string): Date | null {
    const [year, month, day] = value.split('-').map(Number);

    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private formatRestzeit(value: string | null, _abgeschlossen: boolean): string {
    if (!value) {
      return '—';
    }

    if (/^\d+T\s\d{2}:\d{2}$/.test(value) || /^\d{2}:\d{2}$/.test(value)) {
      return value;
    }

    const normalized = value.includes('.') ? value : `0.${value}`;
    const match = /^(?<days>\d+)\.(?<hours>\d{1,2}):(?<minutes>\d{2})/.exec(normalized);

    if (!match?.groups) {
      return value;
    }

    const days = Number(match.groups['days']);
    const hours = Number(match.groups['hours']);
    const minutes = Number(match.groups['minutes']);

    if (days > 0) {
      return `${days}T ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  getBestellungBericht(
    id: string
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.apiBaseUrl}/bestellungen/${id}/bericht`,
      {
        observe: 'response',
        responseType: 'blob',
      }
    );
  }
}